/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import configLoader from "@src/utils/Config";
import endpointStore from "@src/stores/EndpointStore";
import type { Phase } from "@src/utils/TransferProgress";

/** The Swift colours the live views draw with (Ops Console tokens). */
/*
 * The live views' colours, as tokens so they follow the light/dark theme. Brand only (UI spec 8.1):
 * progress and "done" use the brand steel blue and the accent, never traffic-light green or amber;
 * the error red is kept for a failure's single mark.
 */
export const LIVE = {
  slate: "var(--t9-primary)", // var(--t9-primary)
  slateDark: "var(--t9-primary-hover)", // var(--t9-primary-hover)
  slateDeep: "var(--t9-brand-800)", // var(--t9-brand-800)
  cyan: "var(--t9-accent-500)", // var(--t9-accent-500)
  cyanDeep: "var(--t9-info-700)", // var(--t9-info-700)
  green: "var(--t9-brand-500)", // was var(--t9-success-500) (Brand Green): completed reads in the brand steel blue
  greenDeep: "var(--t9-primary-hover)", // was var(--t9-success-700)
  warning: "var(--t9-accent-600)", // was var(--t9-warning-500): attention is an accent outline, not amber
  warningDeep: "var(--t9-info-700)", // was var(--t9-warning-700)
  danger: "var(--t9-danger-700)", // var(--t9-danger-700)
  text: "var(--t9-text)", // var(--t9-text)
  textSecondary: "var(--t9-text-muted)", // var(--t9-text-muted)
  muted: "var(--t9-text-secondary)", // var(--t9-text-secondary)
  faint: "var(--t9-text-faint)", // var(--t9-text-faint)
  border: "var(--t9-border)", // var(--t9-border)
  track: "var(--t9-bg-sunken)", // var(--t9-bg-sunken)
  surface: "var(--t9-bg-surface)", // #FFFFFF
  soft: "var(--t9-bg-muted)", // var(--t9-bg-muted)
  warnSoft: "var(--t9-bg-surface)", // was var(--t9-info-50)
  warnBorder: "var(--t9-accent-500)", // was #FDE7B0
};

/** Provider display names, in Swift's words ("On-prem", never "bare metal"). */
export const providerLabel = (type: string | null | undefined): string => {
  if (!type) {
    return "Unknown";
  }
  if (type === "metal") {
    return "On-prem";
  }
  const names: { [k: string]: string } = (configLoader.config && configLoader.config.providerNames) || {};
  return names[type] || type;
};

export const endpointOf = (id: string | null | undefined) =>
  (id && endpointStore.endpoints.find(e => e.id === id)) || null;

/** "14:32", "Tomorrow 09:10", "Fri 3 Oct 18:00". */
export const formatClock = (at: number | null | undefined, now = Date.now()): string => {
  if (at == null || !Number.isFinite(at)) {
    return "—";
  }
  const d = new Date(at);
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const day = new Date(now);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, day)) {
    return time;
  }
  const tomorrow = new Date(now + 24 * 3600 * 1000);
  if (sameDay(d, tomorrow)) {
    return `Tomorrow ${time}`;
  }
  const yesterday = new Date(now - 24 * 3600 * 1000);
  if (sameDay(d, yesterday)) {
    return `Yesterday ${time}`;
  }
  return `${d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} ${time}`;
};

/** "Just now", "12 min ago", "3 h ago", "2 d ago". */
export const formatAgo = (at: number | null | undefined, now = Date.now()): string => {
  if (at == null) {
    return "—";
  }
  const mins = Math.round((now - at) / 60000);
  if (mins < 1) {
    return "Just now";
  }
  if (mins < 60) {
    return `${mins} min ago`;
  }
  const hours = Math.round(mins / 60);
  if (hours < 24) {
    return `${hours} h ago`;
  }
  return `${Math.round(hours / 24)} d ago`;
};

export type Tone = "info" | "success" | "warning" | "danger" | "neutral" | "brand";

export const phaseTone = (phase: Phase): Tone => {
  switch (phase) {
    case "Completed":
      return "success";
    case "Failed":
      return "danger";
    case "Cancelled":
      return "neutral";
    case "Queued":
      return "warning";
    case "Syncing disks":
      return "info";
    default:
      return "brand";
  }
};

export const TONE_COLOURS: { [t in Tone]: { bg: string; fg: string; dot: string } } = {
  info: { bg: "var(--t9-info-50)", fg: "var(--t9-info-700)", dot: "var(--t9-accent-500)" },
  success: { bg: "var(--t9-selected-bg)", fg: "var(--t9-primary)", dot: "var(--t9-primary)" }, // brand, not green (UI spec 8.1)
  warning: { bg: "var(--t9-bg-surface)", fg: "var(--t9-info-700)", dot: "var(--t9-accent-500)" }, // accent, not amber (UI spec 8.1)
  danger: { bg: "var(--t9-danger-50)", fg: "var(--t9-danger-700)", dot: "var(--t9-danger-700)" },
  neutral: { bg: "var(--t9-bg-sunken)", fg: "var(--t9-text-muted)", dot: "var(--t9-text-faint)" },
  brand: { bg: "var(--t9-selected-bg)", fg: "var(--t9-primary)", dot: "var(--t9-primary)" },
};

export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * How full the target's drive bays are drawn: the bytes copied while disks report; before the
 * copy starts, empty; once the copy is behind the run (OS preparation, deploying, a deployment
 * that works on disks already there), full.
 */
export const targetFill = (p: { bytesPercent: number | null; phase: Phase; hasDiskData: boolean }) => {
  if (p.phase === "Completed") {
    return 100;
  }
  if (p.bytesPercent != null) {
    return p.bytesPercent;
  }
  return ["OS preparation", "Deploying", "Finalising"].includes(p.phase) ? 100 : 0;
};
